import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { compact } from 'lodash-es';
import { GradientIcon, Sidebar, SIDEBAR_ITEM_ACTIVE_CLASS } from 'seagull';
import { AnchorLogo, Gift, PlusCircle } from 'seagull/icons';
import DisabledBillingTooltip from '~/components/DisabledBillingTooltip';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGlobalBillClient } from '~/hooks/useGlobalBillClient';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { notEmpty } from '~/infra/utils';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { BusinessSwitcherForSpecificUsers } from '../BusinessSwitcher/BusinessSwitcher';
import styles from './HomeSidebar.module.scss';
export const sidebarTextsPath = 'components.navigation.sidebar';
export const getSidebarRoutes = (param)=>{
    let { texts, CONNECTED_APPS_PAGE, REFERRAL_DASHBOARD_UPGRADE, FE_CRM } = param;
    return compact([
        {
            id: 'dashboard',
            url: routes.vendorDashboard,
            text: texts.dashboard(),
            shouldShow: (param)=>{
                let { isVendor, hasBusiness } = param;
                return isVendor || !hasBusiness;
            }
        },
        FE_CRM && {
            id: 'clients',
            text: texts.clients(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return isVendor;
            },
            items: [
                {
                    id: 'clients-list',
                    url: routes.clients,
                    text: texts.clients(),
                    shouldShow: (param)=>{
                        let { isVendor } = param;
                        return isVendor;
                    }
                },
                {
                    id: 'contacts',
                    url: routes.contacts,
                    text: texts.contacts(),
                    shouldShow: (param)=>{
                        let { isVendor } = param;
                        return isVendor;
                    },
                    exact: true
                }
            ]
        },
        {
            id: 'proposals',
            text: texts.proposals(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return isVendor;
            },
            items: [
                {
                    id: 'sent',
                    url: routes.proposalsDashboard,
                    text: texts.sent(),
                    shouldShow: (param)=>{
                        let { isVendor } = param;
                        return isVendor;
                    }
                },
                {
                    id: 'drafts',
                    url: routes.draftsDashboard,
                    text: texts.drafts(),
                    shouldShow: (param)=>{
                        let { isVendor } = param;
                        return isVendor;
                    }
                },
                {
                    id: 'archived',
                    url: routes.archivedProposals,
                    text: texts.archived(),
                    shouldShow: (param)=>{
                        let { isVendor } = param;
                        return isVendor;
                    }
                },
                {
                    id: routes.proposalTemplates,
                    text: texts.templates(),
                    url: routes.proposalTemplates,
                    shouldShow: (param)=>{
                        let { isVendor } = param;
                        return isVendor;
                    }
                },
                {
                    id: routes.proposalDefaults,
                    text: texts.defaultSettings(),
                    url: routes.proposalDefaults,
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member;
                    }
                }
            ]
        },
        {
            id: 'agreements',
            url: routes.agreementsDashboard,
            text: texts.agreements(),
            shouldShow: true
        },
        {
            id: 'proposals',
            url: routes.proposalsDashboard,
            text: texts.proposals(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return !isVendor;
            }
        },
        {
            id: 'billing',
            url: routes.globalBillingHub,
            text: texts.billing(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return isVendor;
            }
        },
        {
            id: 'invoices',
            url: routes.invoicesDashboard,
            text: texts.invoices(),
            shouldShow: true
        },
        {
            id: 'payouts',
            url: routes.payoutsDashboard,
            text: texts.payouts(),
            shouldShow: (param)=>{
                let { isVendor, role } = param;
                return isVendor && !!role && role !== UserRole.Member;
            }
        },
        !FE_CRM && {
            id: 'contacts',
            url: routes.contacts,
            text: texts.contacts(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return isVendor;
            },
            exact: true
        },
        {
            text: texts.services(),
            shouldShow: (param)=>{
                let { isVendor, role } = param;
                return isVendor && !!role && role !== UserRole.Member;
            },
            id: 'allServices',
            items: [
                {
                    id: 'services',
                    url: routes.serviceLibrary,
                    text: texts.services(),
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member;
                    },
                    exact: true
                },
                {
                    id: 'bundles',
                    url: routes.bundlesLibrary,
                    text: texts.bundles(),
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member;
                    },
                    exact: true
                }
            ]
        },
        {
            id: 'reports',
            url: routes.reports,
            text: texts.reports(),
            shouldShow: (param)=>{
                let { isVendor, role } = param;
                return isVendor && !!role && role !== UserRole.Member;
            }
        },
        {
            id: 'integrations',
            text: texts.integrations(),
            shouldShow: (param)=>{
                let { isVendor, role } = param;
                return isVendor && !!role && role !== UserRole.Member;
            },
            items: [
                {
                    id: 'available-apps',
                    url: routes.availableApps,
                    text: texts.availableApps(),
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member;
                    }
                },
                {
                    id: 'connected-apps',
                    url: routes.connectedApps,
                    text: texts.connectedApps(),
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member && !!CONNECTED_APPS_PAGE;
                    }
                },
                {
                    id: 'api-keys',
                    url: routes.anchorApiKeys,
                    text: texts.apiKeys(),
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member;
                    }
                }
            ]
        },
        {
            id: 'activity-logs',
            url: routes.activityLogs,
            text: texts.activityLogs(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return !isVendor;
            }
        },
        {
            id: 'settings',
            text: texts.settings(),
            shouldShow: (param)=>{
                let { role } = param;
                return !!role && role !== UserRole.Member;
            },
            items: compact([
                {
                    id: routes.businessDetails,
                    text: texts.businessDetails(),
                    url: routes.businessDetails,
                    shouldShow: (param)=>{
                        let { role } = param;
                        return !!role && role !== UserRole.Member;
                    }
                },
                {
                    id: routes.team,
                    text: texts.team(),
                    url: routes.team,
                    shouldShow: (param)=>{
                        let { role } = param;
                        return !!role && role !== UserRole.Member;
                    }
                },
                {
                    id: routes.notifications,
                    url: routes.notifications,
                    text: texts.notifications(),
                    shouldShow: (param)=>{
                        let { isVendor, role } = param;
                        return isVendor && !!role && role !== UserRole.Member;
                    }
                }
            ])
        },
        {
            id: 'referrals',
            url: routes.referrals,
            text: texts.referAndEarn(),
            shouldShow: (param)=>{
                let { isVendor } = param;
                return isVendor;
            },
            icon: REFERRAL_DASHBOARD_UPGRADE ? /*#__PURE__*/ _jsx(GradientIcon, {
                icon: /*#__PURE__*/ _jsx(Gift, {
                    size: 18
                }),
                animateOnParentHover: true
            }) : /*#__PURE__*/ _jsx(Gift, {
                size: 18
            })
        }
    ]);
};
const shouldBeCollapsed = (currentPath, clickedRouteId, route)=>!(route.id === clickedRouteId) && !('items' in route && route.items.some((item)=>'url' in item && currentPath === item.url));
const SidebarLevel = (param)=>{
    let { items, logBI, disableAutoNavigateGroup } = param;
    const location = useLocation();
    const generateCollapseMap = useCallback((uncollapsedGroupId)=>items.reduce((acc, route)=>Object.assign({}, acc, {
                [route.id]: shouldBeCollapsed(location.pathname, uncollapsedGroupId, route)
            }), {}), [
        items,
        location
    ]);
    const [collapseMap, setCollapseMap] = useState(generateCollapseMap(null));
    useEffect(()=>{
        setCollapseMap(generateCollapseMap(null));
    }, [
        items,
        generateCollapseMap
    ]);
    const renderItem = (item)=>{
        if ('url' in item) {
            return /*#__PURE__*/ _jsx(Sidebar.Item, {
                as: NavLink,
                to: item.url,
                activeClassName: SIDEBAR_ITEM_ACTIVE_CLASS,
                id: item.id,
                after: item.icon || undefined,
                testId: "home-sidebar-".concat(item.id),
                bi: {
                    name: "sidebar_nav_".concat(item.id)
                },
                onClick: (event)=>{
                    logBI(10355, {
                        screenName: event.currentTarget.id
                    });
                    setCollapseMap(generateCollapseMap(null));
                },
                children: item.text
            }, item.id);
        } else if ('items' in item) {
            return /*#__PURE__*/ _createElement(Sidebar.Group, _object_spread_props(_object_spread({}, !disableAutoNavigateGroup && {
                as: NavLink,
                to: 'url' in item.items[0] && item.items[0].url || undefined
            }), {
                testId: "home-sidebar-".concat(item.id, "-toggle"),
                id: item.id,
                key: item.id,
                items: /*#__PURE__*/ _jsx(SidebarLevel, {
                    items: item.items,
                    logBI: logBI,
                    disableAutoNavigateGroup: disableAutoNavigateGroup
                }),
                collapsed: collapseMap[item.id],
                onClick: (_event)=>{
                    logBI(10356, {
                        groupName: item.id
                    });
                    setCollapseMap(generateCollapseMap(!collapseMap[item.id] ? null : item.id));
                }
            }), item.text);
        }
        return null;
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: items.map(renderItem).filter(notEmpty)
    });
};
export default function HomeSidebar(param) {
    let { disableAutoNavigateGroup = false } = param;
    const { t: sidebarTexts } = useLocaleKeys(sidebarTextsPath);
    const isVendor = useStore(selectors.user.isVendor);
    const { openGlobalBillModal } = useGlobalBillClient();
    const { logBI } = useStoreActions();
    const isMobileDevice = useIsMobileForHomePage();
    const { DISABLE_BILLING_SCAN = false, CONNECTED_APPS_PAGE, REFERRAL_DASHBOARD_UPGRADE, FE_CRM } = useFeatureFlags();
    const items = useStore(selectors.filterAvailableRoutes(getSidebarRoutes({
        texts: sidebarTexts,
        CONNECTED_APPS_PAGE,
        REFERRAL_DASHBOARD_UPGRADE,
        FE_CRM
    })));
    const onLinkClick = (event)=>{
        logBI(10355, {
            screenName: event.currentTarget.id
        });
    };
    return /*#__PURE__*/ _jsxs(Sidebar, {
        testId: "home-sidebar",
        children: [
            /*#__PURE__*/ _jsx(Sidebar.Header, {
                children: /*#__PURE__*/ _jsx(NavLink, {
                    id: isVendor ? 'dashboard-via-logo' : 'agreements-via-logo',
                    to: "/",
                    "data-testid": "home-sidebar-logo-btn",
                    onClick: onLinkClick,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.logoContainer,
                        children: /*#__PURE__*/ _jsx(AnchorLogo, {
                            theme: "light",
                            height: 44
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Sidebar.Content, {
                children: /*#__PURE__*/ _jsx(SidebarLevel, {
                    items: items,
                    logBI: logBI,
                    disableAutoNavigateGroup: disableAutoNavigateGroup
                })
            }),
            /*#__PURE__*/ _jsx(Sidebar.Footer, {
                children: isMobileDevice ? /*#__PURE__*/ _jsx(BusinessSwitcherForSpecificUsers, {
                    inSidebar: true
                }) : isVendor && /*#__PURE__*/ _jsx(Sidebar.Item, {
                    id: "add-charge",
                    before: /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 20
                    }),
                    intent: Intent.AddCharge,
                    testId: "home-sidebar-add-charge",
                    bi: {
                        name: 'sidebar_add_charge'
                    },
                    onClick: openGlobalBillModal,
                    disabled: DISABLE_BILLING_SCAN,
                    tooltip: DISABLE_BILLING_SCAN ? {
                        title: /*#__PURE__*/ _jsx(DisabledBillingTooltip, {}),
                        placement: 'right'
                    } : undefined,
                    children: sidebarTexts.billClient()
                })
            })
        ]
    });
}
